"use client";

import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";
import { superscriptInt } from "@/lib/tool-kit/physics-waves/format";

export interface ScaleMark {
  value: number;
  label: string;
}

const WIDTH = 600;
const LEFT = 24;
const SPAN = 552;
const AXIS = 70;

function labelAnchor(x: number): "start" | "middle" | "end" {
  if (x > WIDTH * 0.75) {
    return "end";
  }
  return x < WIDTH * 0.25 ? "start" : "middle";
}

/**
 * A logarithmic length scale with named reference sizes along it and a marker for one
 * value — how a de Broglie wavelength compares with a proton, an atom or visible light.
 */
export function ScaleStrip({
  lowExp,
  highExp,
  marks,
  value,
  valueLabel,
  label,
  description,
  step = 5,
}: {
  lowExp: number;
  highExp: number;
  marks: readonly ScaleMark[];
  value: number;
  valueLabel: string;
  label: string;
  description: string;
  /** Decades between labelled ticks. */
  step?: number;
}) {
  const at = (v: number) =>
    LEFT +
    SPAN *
      Math.min(1, Math.max(0, (Math.log10(v) - lowExp) / (highExp - lowExp)));
  const ticks: number[] = [];
  for (let e = Math.ceil(lowExp / step) * step; e <= highExp; e += step) {
    ticks.push(e);
  }
  const marker = at(value);
  const anchor = labelAnchor(marker);
  return (
    <ChartSvg
      className="mx-auto block h-auto w-full max-w-2xl"
      description={description}
      height={140}
      id="db-scale"
      label={label}
      width={WIDTH}
    >
      <line
        stroke={CHART_INK.axis}
        strokeWidth={2}
        x1={LEFT}
        x2={LEFT + SPAN}
        y1={AXIS}
        y2={AXIS}
      />
      {ticks.map((e) => (
        <g key={e}>
          <line
            stroke={CHART_INK.axis}
            x1={px(at(10 ** e))}
            x2={px(at(10 ** e))}
            y1={AXIS}
            y2={AXIS + 6}
          />
          <text
            fill={CHART_INK.muted}
            fontSize={11}
            textAnchor="middle"
            x={px(at(10 ** e))}
            y={AXIS + 20}
          >
            {`10${superscriptInt(e)} m`}
          </text>
        </g>
      ))}
      {marks.map((mark) => (
        <g key={mark.label}>
          <line
            stroke={CHART_COLORS[2]}
            strokeWidth={2}
            x1={px(at(mark.value))}
            x2={px(at(mark.value))}
            y1={AXIS - 22}
            y2={AXIS}
          />
          <text
            fill={CHART_COLORS[2]}
            fontSize={12}
            textAnchor="middle"
            x={px(at(mark.value))}
            y={AXIS - 28}
          >
            {mark.label}
          </text>
        </g>
      ))}
      <polygon
        fill={CHART_COLORS[3]}
        points={`${px(marker - 7)},${AXIS + 46} ${px(marker + 7)},${AXIS + 46} ${px(marker)},${AXIS + 30}`}
      />
      <text
        fill={CHART_COLORS[3]}
        fontSize={12}
        fontWeight={600}
        textAnchor={anchor}
        x={px(marker)}
        y={AXIS + 62}
      >
        {valueLabel}
      </text>
    </ChartSvg>
  );
}
